import React from 'react';
import styled, { css } from 'astroturf/react';

import { Accordion } from 'js/design_system/Accordion';
import Badge from 'js/design_system/Badge';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { BodyLink } from 'js/design_system/link/BodyLink';

import { CoScientistCta } from './CoScientistCta';
import {
  ClosingCta,
  displayHeading,
  FaqAnswer,
  FaqList,
  LandingFooter,
  Page,
  Section,
  semibold,
} from './shared';

const Hero = styled.section`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm-24, 24px);
  padding: var(--spacing-lg-80, 80px) var(--spacing-lg-64, 64px)
    var(--spacing-lg-64, 64px);
  text-align: center;

  @include breakpoint(medium) {
    padding: var(--spacing-lg-64, 64px) var(--spacing-md-40, 40px);
  }

  @include breakpoint(small) {
    padding: var(--spacing-md-40, 40px) var(--spacing-sm-24, 24px);
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
  max-width: 920px;
  margin-bottom: var(--spacing-xs-16, 16px);
`;

const Plans = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: grid;
  grid-template-columns: repeat(2, minmax(0, 448px));
  justify-content: center;
  gap: var(--spacing-sm-24, 24px);
  width: 100%;
  text-align: start;

  @include breakpoint(small) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PlanCard = styled.article`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--spacing-sm-24, 24px);
  padding: var(--spacing-sm-24, 24px) var(--spacing-sm-32, 32px);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-md, 8px);
`;

const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-24, 24px);
`;

const PlanHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
`;

const Price = styled.p`
  display: flex;
  align-items: baseline;
  gap: var(--spacing-xs-8, 8px);
  margin: 0;
`;

const Features = styled.ul`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-12, 12px);
`;

const secondaryText = css`
  &[class] {
    color: var(--text-secondary, #535366);
  }
`;

const CheckTile = styled.span`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius-radius-round, 104px);
  background: var(--brand-colors-expanded-colors-lime-100, #f2f5ef);
  color: var(--brand-colors-expanded-colors-lime-800, #43523a);
`;

type Plan = {
  name: string;
  priceUnit: string;
  features: string[];
};

const FREE_TRIAL: Plan = {
  name: 'Free trial',
  priceUnit: 'to start',
  features: [
    'Full access to Academia Co-scientist for a limited period',
    'Limited set of proactive suggestions',
    'Your tools, chats and files are saved and remain free to access after trial ends',
  ],
};

const MONTHLY: Plan = {
  name: 'Monthly',
  priceUnit: '/ month',
  features: [
    'Unlimited usage to chat, build tools, analyze data, and more',
    'Ongoing proactive suggestions',
    'Your tools, chats and files are saved and remain free to access anytime',
  ],
};

const PRICING_FAQS: { question: string; answer: string }[] = [
  {
    question: 'How does the free trial work?',
    answer:
      'Sign in with a free Academia.edu account and Co-scientist gets to work on your ' +
      'research. Access all of what Co-scientist has to offer during the free trial: ' +
      'chats, build tools, run analyses, and receive suggestions on how to advance your ' +
      'research.',
  },
  {
    question: 'What happens when my free trial ends?',
    answer:
      'You keep everything. Your chats, files, and the tools Co-scientist built for you ' +
      'stay saved, and you can open them anytime. To start new work, upgrade to Monthly ' +
      'for unlimited use.',
  },
  {
    question: 'Can I cancel anytime? What happens to my account when I cancel?',
    answer:
      'You can cancel your subscription at any time from your Account Settings page. ' +
      'You keep unlimited usage until the end of the period you’ve paid for. After ' +
      'that, your chats, files, and tools remain accessible and free to open.',
  },
];

const PlanCardContent: React.FC<{
  plan: Plan;
  price: string;
  plusTax?: boolean;
  children: React.ReactNode;
}> = ({ plan, price, plusTax = false, children }) => (
  <PlanCard>
    <PlanDetails>
      <PlanHeader>
        <Body as="h2" size="xl" className={semibold}>
          {plan.name}
        </Body>
        <Price>
          <Body as="span" size="xl">
            {price}
          </Body>
          <Body as="span" size="sm" className={secondaryText}>
            {plan.priceUnit}
            {plusTax && ' + tax'}
          </Body>
        </Price>
      </PlanHeader>
      <Features>
        {plan.features.map((feature) => (
          <Feature key={feature}>
            <CheckTile>
              <Icon name="check" size={14} aria-hidden />
            </CheckTile>
            <Body size="md">{feature}</Body>
          </Feature>
        ))}
      </Features>
    </PlanDetails>
    {children}
  </PlanCard>
);

type CoScientistPricingPageProps = {
  freeTrialUrl: string;
  closingCtaUrl: string;
  hasAccess: boolean;
  upgradeUrl: string;
  subscribed: boolean;
  /* Localized, as the Co-scientist checkout would charge this visitor, e.g. "$20", "€18" */
  monthlyPrice: string;
  /* VAT is added at checkout, as in most of Europe */
  monthlyPlusTax: boolean;
  freePrice: string;
  supportUrl: string;
  academiaUrl: string;
  academiaLogoWhite: string;
};

const CoScientistPricingPage: React.FC<CoScientistPricingPageProps> = ({
  freeTrialUrl,
  closingCtaUrl,
  hasAccess,
  upgradeUrl,
  subscribed,
  monthlyPrice,
  monthlyPlusTax,
  freePrice,
  supportUrl,
  academiaUrl,
  academiaLogoWhite,
}) => (
  <Page>
    <Hero>
      <HeroCopy>
        <Badge
          text="Beta pricing"
          color="var(--brand-colors-expanded-colors-turquoise-900, #164c50)"
          backgroundColor="var(--brand-colors-expanded-colors-turquoise-100, #edf4f4)"
          style={{
            border:
              '1px solid var(--brand-colors-expanded-colors-turquoise-200, #d4e6e6)',
          }}
        />
        <Heading
          as="h1"
          fontFamily="serif"
          size="lg"
          className={displayHeading}
        >
          Academia Co-scientist Pricing
        </Heading>
        <Body size="lg">Start free. Upgrade when you’re ready.</Body>
      </HeroCopy>
      <Plans>
        <PlanCardContent plan={FREE_TRIAL} price={freePrice}>
          <CoScientistCta href={freeTrialUrl} size="sm" secondary fullWidth>
            {hasAccess ? 'Open Co-scientist' : 'Start free trial'}
          </CoScientistCta>
        </PlanCardContent>
        <PlanCardContent
          plan={MONTHLY}
          price={monthlyPrice}
          plusTax={monthlyPlusTax}
        >
          <CoScientistCta
            href={upgradeUrl}
            size="sm"
            fullWidth
            secondary={subscribed}
          >
            {subscribed ? 'Manage subscription' : 'Upgrade to Monthly'}
          </CoScientistCta>
        </PlanCardContent>
      </Plans>
      <Body size="md">
        Academia Co-scientist is in Beta. Plans and pricing are subject to
        change at Academia’s discretion.
      </Body>
    </Hero>

    <Section>
      <Heading as="h2" fontFamily="serif" size="lg" className={displayHeading}>
        Pricing FAQ
      </Heading>
      <FaqList>
        <Accordion
          sections={PRICING_FAQS.map((faq) => ({
            title: (
              <Body as="h3" size="lg" className={semibold}>
                {faq.question}
              </Body>
            ),
            contents: <FaqAnswer size="md">{faq.answer}</FaqAnswer>,
          }))}
        />
      </FaqList>
      <Body size="md">
        Still have questions?{' '}
        <BodyLink href={supportUrl} size="md" underline>
          Contact support
        </BodyLink>
      </Body>
    </Section>

    <ClosingCta
      heading="Get started today with the free trial"
      ctaUrl={closingCtaUrl}
      hasAccess={hasAccess}
    />

    <LandingFooter
      academiaUrl={academiaUrl}
      academiaLogoWhite={academiaLogoWhite}
    />
  </Page>
);

export default CoScientistPricingPage;
